import { __assign, __makeTemplateObject, __rest } from "tslib";
import React, { forwardRef, useCallback, useLayoutEffect, useMemo, useRef, } from 'react';
import MuiButton from '@material-ui/core/Button';
import { combineClasses, isRcElement, removeClassName, styled, useDeprecatedCheck, useForkRef, useTheme, useThemeProps, } from '../../../foundation';
import { RcIcon } from '../../Icon';
import { RcIconSizes } from '../../Icon/utils';
import { RcCircularProgress } from '../../Progress';
import { withTooltip } from '../../Tooltip';
import { buttonStyle } from './styles';
import { getButtonIconSize, RcButtonClasses } from './utils';
var _RcButton = forwardRef(function (props, ref) {
    var _a, _b;
    if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useDeprecatedCheck(RcButton, props, [
            {
                prop: 'IconProps',
                time: '2021-4',
                comment: "@deprecated Icon, please use startIcon with `<RcIcon />` */",
            },
        ]);
    }
    var childrenProp = props.children, classesProp = props.classes, startIconProp = props.startIcon, endIconProp = props.endIcon, loading = props.loading, loadingMode = props.loadingMode, disabled = props.disabled, IconProps = props.IconProps, size = props.size, color = props.color, CircularProgressProps = props.CircularProgressProps, variant = props.variant, disabledVariant = props.disabledVariant, radius = props.radius, keepElevation = props.keepElevation, focusVariant = props.focusVariant, disableFocusRippleProp = props.disableFocusRipple, rest = __rest(props, ["children", "classes", "startIcon", "endIcon", "loading", "loadingMode", "disabled", "IconProps", "size", "color", "CircularProgressProps", "variant", "disabledVariant", "radius", "keepElevation", "focusVariant", "disableFocusRipple"]);
    var theme = useTheme();
    var innerRef = useRef(null);
    var buttonRef = useForkRef(innerRef, ref);
    var isPlain = variant === 'plain';
    var isReplace = loadingMode === 'replace';
    var iconSize = getButtonIconSize(size);
    var loadingElm = useMemo(function () {
        if (loading) {
            var progressSize = RcIconSizes[iconSize];
            return (React.createElement(RcCircularProgress, __assign({ size: progressSize, color: "inherit" }, CircularProgressProps)));
        }
        return undefined;
    }, [CircularProgressProps, iconSize, loading]);
    var getCurrIcon = useCallback(function (currIcon) {
        if (isRcElement(currIcon, ['RcIcon']) &&
            currIcon.props.size === RcIcon.defaultProps.size) {
            return React.cloneElement(currIcon, { size: iconSize });
        }
        return currIcon;
    }, [iconSize]);
    var startIcon = useMemo(function () {
        if (loadingElm) {
            switch (loadingMode) {
                case 'prefix':
                    return loadingElm;
                case 'replace':
                    return undefined;
                default:
                    break;
            }
        }
        if (startIconProp) {
            return getCurrIcon(startIconProp);
        }
        // TODO: IconProps will be remove
        if (IconProps) {
            return React.createElement(RcIcon, __assign({ size: iconSize }, IconProps));
        }
        return undefined;
    }, [
        IconProps,
        getCurrIcon,
        iconSize,
        loadingElm,
        loadingMode,
        startIconProp,
    ]);
    var endIcon = useMemo(function () {
        if (loadingElm) {
            switch (loadingMode) {
                case 'suffix':
                    return loadingElm;
                case 'replace':
                    return undefined;
                default:
                    break;
            }
        }
        if (endIconProp) {
            return getCurrIcon(endIconProp);
        }
        return undefined;
    }, [loadingElm, endIconProp, loadingMode, getCurrIcon]);
    var classes = useMemo(function () { return combineClasses(RcButtonClasses, classesProp); }, [classesProp]);
    // * need remove not need MuiButton-iconSizeMedium for icon render correct
    useLayoutEffect(function () {
        removeClassName(innerRef, 'MuiButton-iconSizeMedium');
    });
    var disableFocusRipple = disableFocusRippleProp !== null && disableFocusRippleProp !== void 0 ? disableFocusRippleProp : focusVariant === 'focusRing';
    return (React.createElement(MuiButton, __assign({ ref: buttonRef, disabled: disabled || loading, variant: isPlain ? undefined : variant, disableRipple: ((_b = (_a = theme === null || theme === void 0 ? void 0 : theme.props) === null || _a === void 0 ? void 0 : _a.MuiButton) === null || _b === void 0 ? void 0 : _b.disableRipple) || isPlain, startIcon: startIcon, endIcon: endIcon, classes: classes, disableFocusRipple: disableFocusRipple }, rest), loading && isReplace ? loadingElm : childrenProp));
});
export var RcButtonDefaultSize = 'large';
export var RcButtonDefaultColor = 'primary';
export var RcButtonDefaultVariant = 'contained';
/** @release */
var RcButton = styled(withTooltip(_RcButton)).attrs(function (inProps) {
    var _a = useThemeProps({ props: inProps, name: 'RcButton' }), 
    // @ts-ignore
    theme = _a.theme, 
    // Omit className, prevent duplicate className
    className = _a.className, _b = _a.size, size = _b === void 0 ? RcButtonDefaultSize : _b, _c = _a.color, color = _c === void 0 ? RcButtonDefaultColor : _c, _d = _a.variant, variant = _d === void 0 ? RcButtonDefaultVariant : _d, _e = _a.loadingMode, loadingMode = _e === void 0 ? 'replace' : _e, _f = _a.focusVariant, focusVariant = _f === void 0 ? 'ripple' : _f, rest = __rest(_a, ["theme", "className", "size", "color", "variant", "loadingMode", "focusVariant"]);
    return __assign({ size: size,
        color: color,
        variant: variant,
        loadingMode: loadingMode,
        focusVariant: focusVariant }, rest);
})(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), buttonStyle);
RcButton.displayName = 'RcButton';
export { RcButton };
var templateObject_1;
